import { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { Settings, PanelLeftClose, PanelLeftOpen, LogOut } from "lucide-react";
import { cn } from "@/lib/utils";
import { endSession } from "@/features/account/AccountMenu";
import { matchSection, useNavSections, Flyout, type NavSection } from "@/components/TopNav";
import { canAny, useMe } from "@/lib/me";

const COLLAPSED_KEY = "crm-sidenav-collapsed";

/**
 * Desktop sidebar (sm+). Below sm the TopNav's icon-chip bar takes over —
 * the two are complementary halves of one navigation system, driven by the
 * same SECTIONS config. Sub-pages of the active section render as pills in
 * the TopNav toolbar, not nested here, so the sidebar stays one calm column
 * no matter how many sub-pages a section grows.
 *
 * Collapsible: the bottom toggle shrinks it to an icon rail (tooltips keep
 * items identifiable) and the choice persists per browser.
 */
export function SideNav({ workspaceName }: { workspaceName?: string }) {
  const sections = useNavSections();
  const me = useMe();
  const { pathname } = useLocation();
  // Match by label: useNavSections hands back per-person copies.
  const activeLabel = matchSection(pathname)?.label ?? null;
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem(COLLAPSED_KEY) === "1"
  );
  const toggle = () => {
    setCollapsed((c) => {
      localStorage.setItem(COLLAPSED_KEY, c ? "0" : "1");
      return !c;
    });
  };

  return (
    <aside
      className={cn(
        // No width transition: labels unmount instantly, so animating the
        // width made icons sit centered in a still-wide bar and visibly
        // slide left. An instant snap reads cleaner.
        "hidden sm:flex shrink-0 flex-col border-r border-zinc-800/70 bg-zinc-950",
        collapsed ? "w-14" : "w-52"
      )}
    >
      {/* Logo — same row height as the top bar so the borders line up.
          Expanded: the collapse toggle sits at the row's right edge.
          Collapsed: the logo itself becomes the expand button — hovering
          swaps the bolt for a panel icon (plus a flyout label). */}
      <div
        className={cn(
          "flex h-12 shrink-0 items-center gap-2.5 border-b border-zinc-800/70",
          collapsed ? "justify-center px-0" : "px-4 pr-2"
        )}
      >
        {collapsed ? (
          <button
            type="button"
            onClick={toggle}
            aria-label="Expand menu"
            className="group relative flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-linear-to-br from-indigo-500 to-violet-600 shadow-lg shadow-indigo-500/20 transition-opacity hover:opacity-90"
          >
            <PanelLeftOpen className="h-4 w-4 text-white" />
            <Flyout label="Expand menu" />
          </button>
        ) : (
          <>
            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-linear-to-br from-indigo-500 to-violet-600 shadow-lg shadow-indigo-500/20">
              <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 text-white" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
                <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
              </svg>
            </div>
            <span className="min-w-0 flex-1 truncate text-sm font-semibold tracking-tight text-zinc-100">
              {workspaceName || "CRM"}
            </span>
            <button
              type="button"
              onClick={toggle}
              title="Collapse menu"
              aria-label="Collapse menu"
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-zinc-500 transition-colors hover:bg-zinc-800/40 hover:text-zinc-100"
            >
              <PanelLeftClose className="h-4 w-4" />
            </button>
          </>
        )}
      </div>

      {/* overflow-y-auto would clip the collapsed flyout labels (overflow-x
          can't be visible while overflow-y scrolls), and the icon rail is
          short enough to never need scrolling anyway. */}
      <nav className={cn("flex-1 space-y-0.5 p-2", collapsed ? "overflow-visible" : "overflow-y-auto")}>
        {sections.map((s) => (
          <SideItem
            key={s.defaultTo}
            to={s.defaultTo}
            end={s.defaultTo === "/"}
            label={s.label}
            icon={s.icon}
            collapsed={collapsed}
            forceActive={s.label === activeLabel}
          />
        ))}
      </nav>

      <div className="border-t border-zinc-800/70 p-2">
        {canAny(me, ["settings", "staff"]) && (
          <SideItem to="/settings" label="Settings" icon={Settings} collapsed={collapsed} />
        )}
        {/* Ungated on purpose. Settings is for the people who run the place;
            signing out is for whoever is standing at the desk, and they are
            the ones who most need it on a shared computer. */}
        <button
          type="button"
          data-sign-out
          onClick={() => void endSession()}
          aria-label="Sign out"
          title={collapsed ? "Sign out" : undefined}
          className={cn(
            "group relative flex w-full items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-[13px] font-medium transition-colors",
            "text-zinc-400 hover:bg-rose-500/10 hover:text-rose-300",
            collapsed && "justify-center px-0",
          )}
        >
          <LogOut className="h-4 w-4 shrink-0" />
          {!collapsed && <span className="truncate">Sign out</span>}
        </button>
      </div>
    </aside>
  );
}

function SideItem({
  to, end, label, icon: Icon, collapsed, forceActive,
}: {
  to: string;
  end?: boolean;
  label: string;
  icon: NavSection["icon"];
  collapsed: boolean;
  /** Section active-state comes from matchSection (it knows about sub-page
   *  paths); plain items (Settings) fall back to NavLink's own matching. */
  forceActive?: boolean;
}) {
  return (
    <NavLink
      to={to}
      end={end}
      aria-label={label}
      className={({ isActive }) =>
        cn(
          "group relative flex items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-[13px] font-medium transition-colors",
          collapsed && "justify-center px-0",
          (forceActive ?? isActive)
            ? "bg-zinc-800/70 text-zinc-100"
            : "text-zinc-400 hover:bg-zinc-800/40 hover:text-zinc-100"
        )
      }
    >
      <Icon className="h-4 w-4 shrink-0" />
      {!collapsed && label}
      {collapsed && <Flyout label={label} />}
    </NavLink>
  );
}

